import { LegacyScreenEnum, ScreenEnum } from '../hooks/navigation/Screens';
import lazyWithRetry from '../services/helpers/LazyWithRetry';

const legacyPartnerRedirect = lazyWithRetry(() => import('../navigation/LegacyPartnerRedirect'));

const hasSelectedCompanyMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasSelectedCompanyGuard'));
const hasCompleteOnboardingMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasCompleteOnboardingGuard'),
);
const hasPartnersAccessMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasPartnersAccessGuard'));
const hasManagePartnersAccessMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasManagePartnersAccessGuard'),
);
const hasFeatureFlagPermissionMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasFeatureFlagPermissionGuard'),
);
const hasDeveloperPermissionMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasDeveloperPermissionGuard'),
);
const hasSelectedCompanyOrAdminMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasSelectedCompanyOrAdminGuard'),
);
const isPlgPartnerMiddleware = lazyWithRetry(() => import('../navigation/middleware/IsPlgPartnerGuard'));
const canAccessOnboardingMiddleware = lazyWithRetry(() => import('../navigation/middleware/CanAccessOnboardingGuard'));
const setsStripeDestinationRatesMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/SetsStripeDestinationRatesGuard'),
);
const hasNotSelectedCompanyMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasNotSelectedCompanyGuard'),
);
const hasMerchantBillingMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasMerchantBillingGuard'));
const hasMasterProviderMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasMasterProviderGuard'));
const hasVoiceCallsMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasVoiceCallsGuard'));
const hasConsumerInvoicesMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasConsumerInvoicesGuard'));
const hasWebsiteStudioAccessMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasWebsiteStudioAccessGuard'),
);
// Screens hidden for partners on self-serve (PLG) onboarding; mirrors the
// sidebar `hidden` flags in useNavigation so direct URLs get a notice.
const hasNotPlgOnboardingMiddleware = lazyWithRetry(() => import('../navigation/middleware/HasNotPlgOnboardingGuard'));
const hasNotPlgOnboardingRestaurantMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasNotPlgOnboardingRestaurantGuard'),
);
const hasNotPlgOnboardingUnlessSuperAdminMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasNotPlgOnboardingUnlessSuperAdminGuard'),
);
// The one screen that exists only for a PLG partner's restaurant (My info → Imprint).
const hasPlgOnboardingRestaurantMiddleware = lazyWithRetry(
  () => import('../navigation/middleware/HasPlgOnboardingRestaurantGuard'),
);

const DashboardRoutes = [
  {
    // `/online-shop/` is not a page — it redirects to the canonical landing
    // page below, which is also where the sidebar's Home item points.
    path: ScreenEnum.Home,
    exact: true,
    component: lazyWithRetry(() => import('../views/home/HomeRedirect')),
  },
  {
    path: ScreenEnum.GetStarted,
    component: lazyWithRetry(() => import('../views/getting-started')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    // The canonical landing page. Renders `HomeView`, which dispatches to the
    // right view for the user's permissions (partner empty state, statistics
    // dashboard, customer base, mobile apps) — so it needs no dashboard guard
    // of its own. A guard here would bounce users who cannot read stats back to
    // `/online-shop/`, which now redirects straight back: an infinite loop.
    path: ScreenEnum.Dashboard,
    component: lazyWithRetry(() => import('../views/home')),
  },
  {
    path: ScreenEnum.TopItems,
    component: lazyWithRetry(() => import('../views/top-items')),
    middleware: hasSelectedCompanyMiddleware,
  },
  {
    path: ScreenEnum.GiftCards,
    component: lazyWithRetry(() => import('../views/gift-cards')),
    middleware: hasSelectedCompanyMiddleware,
  },
  {
    // Restored for PLG partners (Artur's round-3 feedback §5): it is the one
    // view that shows a partner their whole portfolio at once, which is
    // exactly what someone running dozens of locations needs.
    path: ScreenEnum.CustomerBase,
    component: lazyWithRetry(() => import('../views/customer-base')),
  },
  {
    path: ScreenEnum.Companies,
    component: lazyWithRetry(() => import('../views/companies')),
  },
  {
    // Standalone partner creation page (FOO-6351). Gated to partners on the
    // new flow, and to having no restaurant selected — with one selected the
    // sidebar offers Onboarding instead, and creating from here would be
    // ambiguous about which restaurant you are working on.
    path: ScreenEnum.NewRestaurant,
    component: lazyWithRetry(() => import('../views/restaurant-onboarding/NewRestaurantPage')),
    middleware: [isPlgPartnerMiddleware, hasNotSelectedCompanyMiddleware],
  },
  {
    // Onboarding for the *selected* restaurant — it resumes at the first
    // incomplete phase. The company comes from the header selection rather than
    // the URL, so `HasSelectedCompanyGuard` redirects home when none is picked.
    // Gated to partners whose provider has plg_onboarding on, plus the
    // restaurant owner such a partner provisioned (two-actor flow) — their
    // "Continue setup" banner/card lands here. Other store users and every
    // non-flagged provider are excluded, exactly as Get Started excludes them.
    path: ScreenEnum.RestaurantOnboarding,
    component: lazyWithRetry(() => import('../views/restaurant-onboarding')),
    middleware: [hasSelectedCompanyMiddleware, canAccessOnboardingMiddleware],
  },
  {
    // No company guard: the queue spans every company in the manager's ACL, so
    // it is meaningful with or without a single company selected.
    path: ScreenEnum.AgentOpsQueue,
    component: lazyWithRetry(() => import('../views/agent-ops-queue')),
  },
  {
    // FoodAmigos-internal tooling: the master guard keeps it off a partner's
    // portal entirely, mirroring the sidebar gate (and, like it, still admitting
    // global-access staff acting on behalf of a partner). The PLG gate lets a
    // super admin through — the flags are needed precisely while a self-serve
    // partner is onboarding restaurants.
    path: ScreenEnum.FeatureFlags,
    component: lazyWithRetry(() => import('../views/feature-flags')),
    middleware: [
      hasMasterProviderMiddleware,
      hasNotPlgOnboardingUnlessSuperAdminMiddleware,
      hasSelectedCompanyMiddleware,
      hasFeatureFlagPermissionMiddleware,
    ],
  },
  {
    // No company guard: partner API accounts span every restaurant of the
    // partner, so the tab is meaningful without a selected company.
    path: ScreenEnum.Developers,
    component: lazyWithRetry(() => import('../views/developers')),
    middleware: [hasDeveloperPermissionMiddleware],
  },
  {
    path: ScreenEnum.Partners,
    component: lazyWithRetry(() => import('../views/partners')),
    middleware: [hasPartnersAccessMiddleware],
  },
  {
    path: ScreenEnum.TestRestaurants,
    component: lazyWithRetry(() => import('../views/test-restaurants')),
    middleware: [hasManagePartnersAccessMiddleware],
  },
  {
    // The Partner section — account, branding, payment gateway, billing and tax
    // defaults. All of them are mounted only for partner (non-master) Admins, via
    // the PartnerAdminScreens gate in canAccessScreen.
    path: ScreenEnum.PartnerAccount,
    component: lazyWithRetry(() => import('../views/partner-account')),
  },
  {
    path: ScreenEnum.PartnerBranding,
    component: lazyWithRetry(() => import('../views/partner-branding')),
  },
  {
    // Partner self-service gateway management — the partner's own payment rails.
    path: ScreenEnum.PartnerPaymentGateway,
    component: lazyWithRetry(() => import('../views/partner-payment-gateway')),
  },
  {
    // Partner self-service billing — payment method + own cycle invoices.
    path: ScreenEnum.PartnerBilling,
    component: lazyWithRetry(() => import('../views/partner-billing')),
  },
  {
    // Partner-level tax category defaults.
    path: ScreenEnum.PartnerTaxCurrency,
    component: lazyWithRetry(() => import('../views/partner-tax-currency')),
  },
  {
    // What the guest service fee earns the partner across its restaurants.
    path: ScreenEnum.PartnerServiceFeeCalculator,
    component: lazyWithRetry(() => import('../views/partner-service-fee-calculator')),
  },
  {
    // A destination partner's default Stripe processing fee. The API refuses
    // everyone else too.
    path: ScreenEnum.PartnerProcessingFees,
    component: lazyWithRetry(() => import('../views/partner-processing-fees')),
    middleware: [setsStripeDestinationRatesMiddleware],
  },
  // The partner pages moved from `/online-shop/partner-*` to their own `/partner`
  // section; these keep the old URLs (and their query strings) working. See
  // LegacyPartnerRedirect for why Stripe makes this more than a courtesy.
  ...Object.values(LegacyScreenEnum).map((path) => ({
    path,
    exact: true,
    component: legacyPartnerRedirect,
  })),
  {
    // No company guard: the page serves both the company menu (company-scoped)
    // and the franchise/master menu (franchise-scoped), like the master menu
    // routes. Scoping is handled inside the view via its `ready` gate.
    path: ScreenEnum.MenuEditHistory,
    component: lazyWithRetry(() => import('../views/menu/menu-edit-history')),
  },
  {
    path: ScreenEnum.PaymentGateways,
    component: lazyWithRetry(() => import('../views/payment-gateways')),
  },
  {
    path: ScreenEnum.ExternalDeliverySupport,
    component: lazyWithRetry(() => import('../views/external-delivery-support')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.SetupPackageFulfillments,
    component: lazyWithRetry(() => import('../views/going-live/setup-package-fulfillments')),
  },
  {
    path: ScreenEnum.Franchises,
    component: lazyWithRetry(() => import('../views/franchises')),
  },
  {
    path: ScreenEnum.WebsiteStorefront,
    component: lazyWithRetry(() => import('../views/website/storefront')),
    middleware: hasSelectedCompanyMiddleware,
  },
  {
    path: ScreenEnum.WebsiteStudio,
    component: lazyWithRetry(() => import('../views/website/studio')),
    middleware: [hasWebsiteStudioAccessMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.WebsiteStudioV2,
    component: lazyWithRetry(() => import('../views/website/studio-v2')),
    middleware: [hasWebsiteStudioAccessMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingOverview,
    component: lazyWithRetry(() => import('../views/marketing/overview')),
  },
  {
    path: ScreenEnum.MarketingFollowers,
    component: lazyWithRetry(() => import('../views/marketing/followers')),
    middleware: [hasSelectedCompanyMiddleware],
    exact: true,
  },
  {
    path: ScreenEnum.MarketingFollowersImport,
    component: lazyWithRetry(() => import('../views/marketing/followers/import')),
    middleware: [hasSelectedCompanyMiddleware],
    exact: true,
  },
  {
    path: `${ScreenEnum.MarketingFollowers}/:id`,
    component: lazyWithRetry(() => import('../views/marketing/followers/follower-details')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    // Every restaurant's tickets: no selected company needed.
    path: ScreenEnum.ReportTechIssueOverview,
    component: lazyWithRetry(() => import('../views/report-tech-issue/overview')),
    exact: true,
  },
  {
    // Without a selected restaurant the page asks for one, so no guard here.
    path: ScreenEnum.ReportTechIssue,
    component: lazyWithRetry(() => import('../views/report-tech-issue')),
    exact: true,
  },
  {
    path: ScreenEnum.DataDeletionRequests,
    component: lazyWithRetry(() => import('../views/marketing/data-deletion-requests')),
    // Admins can review deletion requests across all restaurants without selecting a company.
    // Hidden from self-serve (PLG) partners only, mirroring the sidebar gate:
    // partners on the classic setup have always actioned their own requests.
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyOrAdminMiddleware],
  },
  {
    path: ScreenEnum.MarketingEmailMessages,
    component: lazyWithRetry(() => import('../views/marketing/email-messages')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: `${ScreenEnum.MarketingEmailTemplate}/:id`,
    component: lazyWithRetry(() => import('../views/marketing/email-template')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingPromoCodeDetails,
    component: lazyWithRetry(() => import('../views/marketing/promo-codes/details')),
    middleware: [hasSelectedCompanyMiddleware],
    exact: true,
  },
  {
    path: ScreenEnum.MarketingPromoCodes,
    component: lazyWithRetry(() => import('../views/marketing/promo-codes')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingGoogleReviewProgram,
    component: lazyWithRetry(() => import('../views/marketing/google-review-program')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingGoogleBusiness,
    component: lazyWithRetry(() => import('../views/marketing/google-business')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingLoyaltyProgram,
    component: lazyWithRetry(() => import('../views/marketing/loyalty-program')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingPolygraphyProducts,
    component: lazyWithRetry(() => import('../views/marketing/polygraphy/products')),
    middleware: [hasSelectedCompanyMiddleware, hasMasterProviderMiddleware],
  },
  {
    path: ScreenEnum.MarketingPolygraphyOrders,
    component: lazyWithRetry(() => import('../views/marketing/polygraphy/orders')),
    middleware: [hasSelectedCompanyMiddleware, hasMasterProviderMiddleware],
  },
  {
    path: ScreenEnum.MarketingTrackingLinks,
    component: lazyWithRetry(() => import('../views/marketing/tracking-links')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingGoogleAds,
    component: lazyWithRetry(() => import('../views/marketing/google-ads')),
    middleware: [hasNotPlgOnboardingMiddleware],
    exact: true,
  },
  {
    path: `${ScreenEnum.MarketingGoogleAds}/:id`,
    component: lazyWithRetry(() => import('../views/marketing/google-ads/campaign-details')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.MarketingWebsiteAnalytics,
    component: lazyWithRetry(() => import('../views/marketing/website-analytics')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingFeaturedPosts,
    component: lazyWithRetry(() => import('../views/marketing/featured-posts')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    exact: true,
    path: ScreenEnum.Offers,
    component: lazyWithRetry(() => import('../views/menu/offers')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.CreateOffers,
    component: lazyWithRetry(() => import('../views/menu/offers/components/create-offer/CreateOffer')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MasterCreateOffers,
    component: lazyWithRetry(() => import('../views/menu/offers/components/create-offer/CreateOffer')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.UpdateOffers,
    component: lazyWithRetry(() => import('../views/menu/offers/components/update-offer/UpdateOffer')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MasterUpdateOffers,
    component: lazyWithRetry(() => import('../views/menu/offers/components/update-offer/UpdateOffer')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MarketingPushNotificationDetails,
    component: lazyWithRetry(() => import('../views/marketing/push-notifications/details')),
    middleware: [hasSelectedCompanyMiddleware],
    exact: true,
  },
  {
    path: ScreenEnum.MarketingPushNotifications,
    component: lazyWithRetry(() => import('../views/marketing/push-notifications')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.ReservationsOverview,
    component: lazyWithRetry(() => import('../views/reservations/overview')),
  },
  {
    path: ScreenEnum.ReservationsCreate,
    component: lazyWithRetry(() => import('../views/reservations/create-reservation')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.ReservationsSettings,
    component: lazyWithRetry(() => import('../views/reservations/settings/index')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.PhoneAnswering,
    component: lazyWithRetry(() => import('../views/phone-answering')),
    middleware: [hasSelectedCompanyMiddleware, hasVoiceCallsMiddleware],
  },
  {
    path: ScreenEnum.ReservationsBookingAvailability,
    component: lazyWithRetry(() => import('../views/reservations/booking-availability/index')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.SubscriptionProducts,
    component: lazyWithRetry(() => import('../views/subscriptions/products')),
    middleware: [hasNotPlgOnboardingMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.SalesOrders,
    component: lazyWithRetry(() => import('../views/sales/orders')),
  },
  {
    path: ScreenEnum.SalesReceipts,
    component: lazyWithRetry(() => import('../views/sales/receipts')),
    middleware: [hasSelectedCompanyMiddleware, hasConsumerInvoicesMiddleware],
  },
  {
    path: ScreenEnum.SalesExternalDeliveries,
    component: lazyWithRetry(() => import('../views/sales/external-deliveries')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.BillingMonthlyStatements,
    component: lazyWithRetry(() => import('../views/billing/monthly-statements')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.BillingPayouts,
    component: lazyWithRetry(() => import('../views/billing/payouts')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.BillingInvoices,
    component: lazyWithRetry(() => import('../views/billing/invoices')),
    middleware: [hasNotPlgOnboardingMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.BillingSubscription,
    component: lazyWithRetry(() => import('../views/billing/subscription')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.BillingIncomeReports,
    component: lazyWithRetry(() => import('../views/billing/income-reports')),
    middleware: [hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.BillingTaxReports,
    component: lazyWithRetry(() => import('../views/billing/tax-reports')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.BillingSalesSummary,
    component: lazyWithRetry(() => import('../views/billing/sales-summary')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.Menu,
    component: lazyWithRetry(() => import('../views/menu/menu')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.Modifiers,
    component: lazyWithRetry(() => import('../views/menu/modifiers')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.SmartPricing,
    component: lazyWithRetry(() => import('../views/menu/smart-pricing')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.SoldOutItems,
    component: lazyWithRetry(() => import('../views/menu/sold-out-items')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MenuImport,
    component: lazyWithRetry(() => import('../views/menu/import')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MasterOverview,
    component: lazyWithRetry(() => import('../views/menu/master-overview')),
  },
  {
    path: ScreenEnum.MasterMenu,
    component: lazyWithRetry(() => import('../views/menu/menu')),
  },
  {
    path: ScreenEnum.MasterModifiers,
    component: lazyWithRetry(() => import('../views/menu/modifiers')),
  },
  {
    path: ScreenEnum.MasterSmartPricing,
    component: lazyWithRetry(() => import('../views/menu/smart-pricing')),
  },
  {
    path: ScreenEnum.MasterMenuImport,
    component: lazyWithRetry(() => import('../views/menu/import')),
  },
  {
    path: ScreenEnum.MasterOffers,
    component: lazyWithRetry(() => import('../views/menu/offers')),
  },
  {
    path: ScreenEnum.MyStoreSettings,
    component: lazyWithRetry(() => import('../views/my-store/settings')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreGallery,
    component: lazyWithRetry(() => import('../views/my-store/gallery')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreBusinessInfo,
    component: lazyWithRetry(() => import('../views/my-store/business-info')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreDocuments,
    component: lazyWithRetry(() => import('../views/my-store/documents')),
    middleware: [hasNotPlgOnboardingRestaurantMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStorePersonalInfo,
    component: lazyWithRetry(() => import('../views/my-store/personal-info')),
    middleware: [hasNotPlgOnboardingRestaurantMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreBankInfo,
    component: lazyWithRetry(() => import('../views/my-store/bank-details')),
    middleware: [hasNotPlgOnboardingRestaurantMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreTaxInfo,
    component: lazyWithRetry(() => import('../views/my-store/tax-details')),
    middleware: [hasNotPlgOnboardingRestaurantMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreShippingInfo,
    component: lazyWithRetry(() => import('../views/my-store/shipping-info')),
    middleware: [hasSelectedCompanyMiddleware, hasMasterProviderMiddleware],
  },
  {
    path: ScreenEnum.MyStoreGoogleProfile,
    component: lazyWithRetry(() => import('../views/my-store/google-profile')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreSeo,
    component: lazyWithRetry(() => import('../views/my-store/seo')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreImpressum,
    component: lazyWithRetry(() => import('../views/my-store/impressum')),
    // A PLG partner's restaurant pastes its own imprint (below) and nothing is generated
    // from these fields for it, so the direct URL closes like the sidebar entry.
    middleware: [hasNotPlgOnboardingRestaurantMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreImprint,
    component: lazyWithRetry(() => import('../views/my-store/imprint')),
    middleware: [hasSelectedCompanyMiddleware, hasPlgOnboardingRestaurantMiddleware],
  },
  {
    path: ScreenEnum.MyStorePostLaunch,
    component: lazyWithRetry(() => import('../views/my-store/post-launch')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MyStoreTaxCurrency,
    component: lazyWithRetry(() => import('../views/my-store/tax-currency')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OpeningHours,
    component: lazyWithRetry(() => import('../views/opening-hours/opening-hours')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.Locations,
    component: lazyWithRetry(() => import('../views/opening-hours/locations')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsTakeout,
    component: lazyWithRetry(() => import('../views/order-settings/takeout')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsMissedOrders,
    component: lazyWithRetry(() => import('../views/order-settings/missed-orders')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsOrderLayout,
    component: lazyWithRetry(() => import('../views/order-settings/order-layout')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsPaymentMethods,
    component: lazyWithRetry(() => import('../views/order-settings/payment-methods')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsConnectPaypal,
    component: lazyWithRetry(() => import('../views/order-settings/connect-paypal')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsTipSettings,
    component: lazyWithRetry(() => import('../views/order-settings/tips')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsServiceFee,
    component: lazyWithRetry(() => import('../views/order-settings/service-fee')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsProcessingFees,
    component: lazyWithRetry(() => import('../views/order-settings/processing-fees')),
    middleware: [hasSelectedCompanyMiddleware, setsStripeDestinationRatesMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsTerminal,
    component: lazyWithRetry(() => import('../views/order-settings/terminal')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsReceiptSettings,
    component: lazyWithRetry(() => import('../views/order-settings/receipts')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsInvoicesSettings,
    component: lazyWithRetry(() => import('../views/order-settings/invoices')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware, hasConsumerInvoicesMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsStaffCredentials,
    component: lazyWithRetry(() => import('../views/order-settings/staff-credentials')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.OrderSettingsIntegrations,
    component: lazyWithRetry(() => import('../views/order-settings/integrations')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.LoyaltyDashboard,
    component: lazyWithRetry(() => import('../views/loyalty-tablet/dashboard')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.BillingPaymentInfo,
    component: lazyWithRetry(() => import('../views/billing/payment-info')),
    middleware: [hasSelectedCompanyMiddleware, hasCompleteOnboardingMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.ContractAgreement,
    component: lazyWithRetry(() => import('../views/contract/agreement')),
    middleware: [hasNotPlgOnboardingMiddleware, hasSelectedCompanyMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.ContractTemplate,
    component: lazyWithRetry(() => import('../views/contract/template')),
    middleware: [hasNotPlgOnboardingMiddleware, hasMerchantBillingMiddleware],
  },
  {
    path: ScreenEnum.TeamManagers,
    component: lazyWithRetry(() => import('../views/team/managers')),
  },
  {
    path: ScreenEnum.TeamReports,
    component: lazyWithRetry(() => import('../views/team/reports')),
    middleware: [hasNotPlgOnboardingMiddleware],
  },
  {
    path: ScreenEnum.Profile,
    component: lazyWithRetry(() => import('../views/profile')),
    middleware: [hasSelectedCompanyMiddleware],
  },
  {
    path: ScreenEnum.MobileAppOverview,
    component: lazyWithRetry(() => import('../views/mobile-app/overview')),
  },
  {
    path: ScreenEnum.MobileAppUpdates,
    component: lazyWithRetry(() => import('../views/mobile-app/updates')),
  },
  {
    path: ScreenEnum.MobileAppIntroScreen,
    component: lazyWithRetry(() => import('../views/mobile-app/intro-screen')),
  },
];

export default DashboardRoutes;
